import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import './TaskSummaryCard.css';
import { useTasks, useCompleteTask, useSkipTask } from '../../hooks/useTasks';
import { useAiSuggestions, useAcceptSuggestion, useDismissSuggestion } from '../../hooks/useAiSuggestions';
import { Task, TaskPriority, isDosingPlanTask } from '../../types/task';
import { AiSuggestion } from '../../types/aiSuggestion';
import { Heading } from '../atoms/Typography/Heading';
import { Button } from '../atoms/Button/Button';
import { Badge } from '../atoms/Badge/Badge';
import { Alert } from '../atoms/Alert/Alert';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { EmptyState } from '../atoms/EmptyState';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import type { BadgeTone } from '../atoms/Badge/Badge.types';
import { useTranslation } from '../../i18n/useTranslation';

interface TaskSummaryCardProps {
  aquariumId?: string;
}

const KNOWN_PRIORITIES: ReadonlySet<string> = new Set(['low', 'medium', 'high', 'critical']);

/** Priority is shown as a text badge, never as colour alone (WCAG 1.4.1). */
function getPriorityTone(priority: TaskPriority): BadgeTone {
  switch (priority) {
    case 'critical':
    case 'high': return 'danger';
    case 'medium': return 'warning';
    case 'low': return 'success';
    default: return 'neutral';
  }
}

export function TaskSummaryCard({ aquariumId }: TaskSummaryCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    grouped,
    isLoading: tasksLoading,
    error: tasksError,
    refetch: refetchTasks,
  } = useTasks(aquariumId ?? '');

  const { mutateAsync: completeTaskAsync, isPending: isCompleting } = useCompleteTask(aquariumId);
  const { mutateAsync: skipTaskAsync, isPending: isSkipping } = useSkipTask(aquariumId);

  const {
    suggestions,
    isLoading: suggestionsLoading,
  } = useAiSuggestions({ aquariumId: aquariumId ?? '' });

  const { acceptSuggestion: acceptSuggestionAsync, isPending: isAccepting } = useAcceptSuggestion();
  const { dismissSuggestion: dismissSuggestionAsync, isPending: isDismissing } = useDismissSuggestion();

  const [actionTarget, setActionTarget] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const overdueTasks = grouped?.overdue ?? [];
  const todayTasks = grouped?.today ?? [];
  const allUpcomingTasks = grouped?.upcoming ?? [];
  // Routine doses must not push maintenance (water change, filter) out of the three
  // preview slots (audit §5.2): other tasks first, each group in date order.
  const upcomingTasks = [
    ...allUpcomingTasks.filter((task) => !isDosingPlanTask(task)),
    ...allUpcomingTasks.filter((task) => isDosingPlanTask(task)),
  ].slice(0, 3);
  const pendingSuggestions = (suggestions ?? []).filter((s) => s.status === 'suggested');

  const isLoading = tasksLoading || suggestionsLoading;

  /** Runs one row action; a failure is shown as a translated Alert, never thrown away. */
  async function runAction(targetId: string, action: () => Promise<unknown>) {
    setActionTarget(targetId);
    setActionError(null);
    try {
      await action();
    } catch {
      setActionError(t('dashboard.tasks.actionFailed'));
    } finally {
      setActionTarget(null);
    }
  }

  const handleComplete = (taskId: string) => runAction(taskId, () => completeTaskAsync({ taskId }));
  const handleSkip = (taskId: string) => runAction(taskId, () => skipTaskAsync({ taskId }));
  const handleAccept = (suggestionId: string) =>
    runAction(suggestionId, () => acceptSuggestionAsync({ suggestionId, aquariumId: aquariumId ?? '' }));
  const handleDismiss = (suggestionId: string) =>
    runAction(suggestionId, () => dismissSuggestionAsync({ suggestionId, aquariumId: aquariumId ?? '' }));

  const header = (
    <div className="task-summary-card__header">
      <Heading level={3} className="task-summary-card__title">{t('dashboard.tasks.title')}</Heading>
      {aquariumId && (
        <Link
          to={`/aquariums/${aquariumId}/tasks`}
          className="task-summary-card__view-all"
          data-testid="view-all-tasks-link"
        >
          {t('dashboard.tasks.viewAll')}
        </Link>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className="task-summary-card task-summary-card--loading" data-testid="task-summary-card-loading">
        {header}
        <LiveRegion visuallyHidden busy>{t('dashboard.tasks.loading')}</LiveRegion>
        <div className="task-summary-card__skeleton" aria-hidden="true">
          <div className="task-summary-card__skeleton-row" />
          <div className="task-summary-card__skeleton-row" />
          <div className="task-summary-card__skeleton-row" />
        </div>
      </div>
    );
  }

  if (tasksError != null) {
    return (
      <div className="task-summary-card" data-testid="task-summary-card">
        {header}
        <LoadErrorState
          compact
          title={t('dashboard.tasks.loadError')}
          live="polite"
          onRetry={() => void refetchTasks?.()}
        />
      </div>
    );
  }

  const isEmpty =
    overdueTasks.length === 0 &&
    todayTasks.length === 0 &&
    upcomingTasks.length === 0 &&
    pendingSuggestions.length === 0;

  return (
    <div className="task-summary-card" data-testid="task-summary-card">
      {header}

      {/* Summary counts */}
      <div className="task-summary-card__counts" data-testid="task-summary-counts">
        <div
          className={`task-summary-card__count-item${overdueTasks.length > 0 ? ' task-summary-card__count-item--danger' : ''}`}
          data-testid="overdue-count"
        >
          <span className="task-summary-card__count-number">{overdueTasks.length}</span>
          <span className="task-summary-card__count-label">{t('dashboard.tasks.countOverdue')}</span>
        </div>
        <div
          className={`task-summary-card__count-item${todayTasks.length > 0 ? ' task-summary-card__count-item--warning' : ''}`}
          data-testid="today-count"
        >
          <span className="task-summary-card__count-number">{todayTasks.length}</span>
          <span className="task-summary-card__count-label">{t('dashboard.tasks.countToday')}</span>
        </div>
        <div className="task-summary-card__count-item" data-testid="upcoming-count">
          <span className="task-summary-card__count-number">{allUpcomingTasks.length}</span>
          <span className="task-summary-card__count-label">{t('dashboard.tasks.countUpcoming')}</span>
        </div>
        {pendingSuggestions.length > 0 && (
          <div
            className="task-summary-card__count-item task-summary-card__count-item--ai"
            data-testid="suggestions-count"
          >
            <span className="task-summary-card__count-number">{pendingSuggestions.length}</span>
            <span className="task-summary-card__count-label">{t('dashboard.tasks.countAi')}</span>
          </div>
        )}
      </div>

      {actionError !== null && (
        <Alert compact>
          {actionError}
        </Alert>
      )}

      {/* Overdue section */}
      {overdueTasks.length > 0 && (
        <div className="task-summary-card__section" data-testid="overdue-section">
          <div className="task-summary-card__section-header" data-testid="overdue-section-toggle">
            <span className="task-summary-card__section-label task-summary-card__section-label--danger">
              <span aria-hidden="true">⚠ </span>
              {t('dashboard.tasks.sectionOverdue', { count: overdueTasks.length })}
            </span>
          </div>
          <ul className="task-summary-card__task-list" data-testid="overdue-task-list">
            {overdueTasks.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                onComplete={handleComplete}
                onSkip={handleSkip}
                isActioning={actionTarget === task.id && (isCompleting || isSkipping)}
              />
            ))}
          </ul>
        </div>
      )}

      {/* Today section */}
      {todayTasks.length > 0 && (
        <div className="task-summary-card__section" data-testid="today-section">
          <div className="task-summary-card__section-header" data-testid="today-section-toggle">
            <span className="task-summary-card__section-label task-summary-card__section-label--warning">
              <span aria-hidden="true">📅 </span>
              {t('dashboard.tasks.sectionToday', { count: todayTasks.length })}
            </span>
          </div>
          <ul className="task-summary-card__task-list" data-testid="today-task-list">
            {todayTasks.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                onComplete={handleComplete}
                onSkip={handleSkip}
                isActioning={actionTarget === task.id && (isCompleting || isSkipping)}
              />
            ))}
          </ul>
        </div>
      )}

      {/* Upcoming section */}
      {upcomingTasks.length > 0 && (
        <div className="task-summary-card__section" data-testid="upcoming-section">
          <div className="task-summary-card__section-header" data-testid="upcoming-section-toggle">
            <span className="task-summary-card__section-label">
              <span aria-hidden="true">🔜 </span>
              {t('dashboard.tasks.sectionUpcoming')}
            </span>
          </div>
          <ul className="task-summary-card__task-list" data-testid="upcoming-task-list">
            {upcomingTasks.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                onComplete={handleComplete}
                onSkip={handleSkip}
                isActioning={actionTarget === task.id && (isCompleting || isSkipping)}
              />
            ))}
          </ul>
        </div>
      )}

      {/* AI Suggestions section */}
      {pendingSuggestions.length > 0 && (
        <div className="task-summary-card__section" data-testid="ai-suggestions-section">
          <div className="task-summary-card__section-header" data-testid="ai-section-toggle">
            <span className="task-summary-card__section-label task-summary-card__section-label--ai">
              <span aria-hidden="true">🤖 </span>
              {t('dashboard.tasks.sectionAi', { count: pendingSuggestions.length })}
            </span>
          </div>
          <ul className="task-summary-card__suggestion-list" data-testid="ai-suggestion-list">
            {pendingSuggestions.map((suggestion) => (
              <SuggestionRow
                key={suggestion.id}
                suggestion={suggestion}
                onAccept={handleAccept}
                onDismiss={handleDismiss}
                isActioning={actionTarget === suggestion.id && (isAccepting || isDismissing)}
              />
            ))}
          </ul>
        </div>
      )}

      {isEmpty && (
        <div data-testid="task-summary-empty">
          <EmptyState
            headingLevel={4}
            icon={<CheckCircle2 size={32} />}
            headline={t('dashboard.tasks.emptyTitle')}
            description={t('dashboard.tasks.emptyDescription')}
            actionLabel={aquariumId ? t('dashboard.tasks.emptyAction') : undefined}
            onAction={aquariumId ? () => navigate(`/aquariums/${aquariumId}/tasks`) : undefined}
            actionVariant="secondary"
          />
        </div>
      )}
    </div>
  );
}

interface TaskRowProps {
  task: Task;
  onComplete: (id: string) => Promise<void>;
  onSkip: (id: string) => Promise<void>;
  isActioning: boolean;
}

function TaskRow({ task, onComplete, onSkip, isActioning }: TaskRowProps) {
  const { t, i18n } = useTranslation();
  const dueDate = task.dueDate ? new Date(task.dueDate) : null;
  const dueDateStr = dueDate && !Number.isNaN(dueDate.getTime())
    ? new Intl.DateTimeFormat(i18n.language, { month: 'short', day: 'numeric' }).format(dueDate)
    : null;

  return (
    <li className="task-summary-card__task-row" data-testid={`task-row-${task.id}`}>
      <div className="task-summary-card__task-info">
        <Badge
          tone={getPriorityTone(task.priority)}
          className="task-summary-card__task-priority"
          data-testid={`task-priority-${task.id}`}
        >
          {t(`tasks.priorities.${task.priority}`)}
        </Badge>
        <div className="task-summary-card__task-text">
          <span className="task-summary-card__task-title" data-testid={`task-title-${task.id}`}>
            {task.title}
          </span>
          {dueDateStr && (
            <span className="task-summary-card__task-due" data-testid={`task-due-${task.id}`}>
              {dueDateStr}
            </span>
          )}
        </div>
      </div>
      <div className="task-summary-card__task-actions">
        <Button
          variant="ghost"
          className="task-summary-card__action-btn task-summary-card__action-btn--complete"
          onClick={() => void onComplete(task.id)}
          disabled={isActioning}
          aria-label={t('dashboard.tasks.completeAria', { title: task.title })}
          data-testid={`complete-task-${task.id}`}
          title={t('dashboard.tasks.complete')}
        >
          <span aria-hidden="true">✓</span>
        </Button>
        <Button
          variant="ghost"
          className="task-summary-card__action-btn task-summary-card__action-btn--skip"
          onClick={() => void onSkip(task.id)}
          disabled={isActioning}
          aria-label={t('dashboard.tasks.skipAria', { title: task.title })}
          data-testid={`skip-task-${task.id}`}
          title={t('dashboard.tasks.skip')}
        >
          <span aria-hidden="true">⏭</span>
        </Button>
      </div>
    </li>
  );
}

interface SuggestionRowProps {
  suggestion: AiSuggestion;
  onAccept: (id: string) => Promise<void>;
  onDismiss: (id: string) => Promise<void>;
  isActioning: boolean;
}

function SuggestionRow({ suggestion, onAccept, onDismiss, isActioning }: SuggestionRowProps) {
  const { t } = useTranslation();
  const priority = suggestion.priority && KNOWN_PRIORITIES.has(suggestion.priority)
    ? (suggestion.priority as TaskPriority)
    : null;
  return (
    <li
      className="task-summary-card__suggestion-row"
      data-testid={`suggestion-row-${suggestion.id}`}
    >
      <div className="task-summary-card__suggestion-info">
        <span
          className="task-summary-card__suggestion-icon"
          aria-hidden="true"
        >
          🤖
        </span>
        <div className="task-summary-card__suggestion-text">
          <span
            className="task-summary-card__suggestion-title"
            data-testid={`suggestion-title-${suggestion.id}`}
          >
            {suggestion.title}
          </span>
          {priority !== null && (
            <Badge
              tone={getPriorityTone(priority)}
              className="task-summary-card__suggestion-priority"
              data-testid={`suggestion-priority-${suggestion.id}`}
            >
              {t(`tasks.priorities.${priority}`)}
            </Badge>
          )}
        </div>
      </div>
      <div className="task-summary-card__suggestion-actions">
        <Button
          variant="ghost"
          className="task-summary-card__action-btn task-summary-card__action-btn--accept"
          onClick={() => void onAccept(suggestion.id)}
          disabled={isActioning}
          aria-label={t('dashboard.tasks.acceptAria', { title: suggestion.title })}
          data-testid={`accept-suggestion-${suggestion.id}`}
          title={t('dashboard.tasks.accept')}
        >
          <span aria-hidden="true">✓</span>
        </Button>
        <Button
          variant="ghost"
          className="task-summary-card__action-btn task-summary-card__action-btn--dismiss"
          onClick={() => void onDismiss(suggestion.id)}
          disabled={isActioning}
          aria-label={t('dashboard.tasks.dismissAria', { title: suggestion.title })}
          data-testid={`dismiss-suggestion-${suggestion.id}`}
          title={t('dashboard.tasks.dismiss')}
        >
          <span aria-hidden="true">✕</span>
        </Button>
      </div>
    </li>
  );
}

export default TaskSummaryCard;
